import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { ExplainerButton } from '../legislation/LegislationExplainer';

interface LegislationItem {
  regulation_number: string;
  application_date: string;
  entry_into_force_date: string | null;
  regulation_url: string | null;
  tier2hazardnames: string[];
}

/** Draft act from the legislative pipeline (Comitology Register dossier)
 *  with an expected or confirmed future application date. */
interface PipelineUpcomingItem {
  dossier_key: string;
  title: string | null;
  stage: string;
  application_date: string;
  application_confirmed: boolean;
  act_type: string | null;
  basic_act_ref: string | null;
  impact_level: string | null;
  hazard_name: string | null;
}

interface UpcomingResponse {
  upcoming: LegislationItem[];
  pipeline: PipelineUpcomingItem[];
}

type TimelineEntry =
  | { kind: 'mrl'; date: string; mrl: LegislationItem }
  | { kind: 'pipeline'; date: string; pipeline: PipelineUpcomingItem };

async function fetchUpcoming(): Promise<UpcomingResponse> {
  const res = await fetch(`${API_BASE_URL}/api/upcoming-legislation`);
  if (!res.ok) throw new Error(await res.text());
  const json = await res.json();
  if (!json.ok) throw new Error('Server responded ok: false');
  return { upcoming: json.upcoming ?? [], pipeline: json.pipeline ?? [] };
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const now = new Date();
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function urgencyClass(days: number): string {
  if (days <= 30) return 'leg-timeline__dot--urgent';
  if (days <= 90) return 'leg-timeline__dot--soon';
  return 'leg-timeline__dot--later';
}

const PIPELINE_STAGE_LABELS: Record<string, string> = {
  feedback_open: 'feedback open',
  tabled: 'tabled',
  voted_positive: 'voted',
  no_opinion: 'no opinion',
  scrutiny: 'in scrutiny',
  adopted: 'adopted',
  published: 'published',
  in_progress: 'in progress',
};

export default function UpcomingLegislationCard() {
  const { data, isLoading, isError } = useQuery(
    ['upcomingLegislation'],
    fetchUpcoming,
  );

  // One timeline: published MRL regulations (confirmed dates) and pipeline
  // drafts (expected dates, marked as such), sorted by application date.
  const entries: TimelineEntry[] = [];
  if (data) {
    for (const item of data.upcoming) entries.push({ kind: 'mrl', date: item.application_date, mrl: item });
    for (const item of data.pipeline) entries.push({ kind: 'pipeline', date: item.application_date, pipeline: item });
    entries.sort((a, b) => a.date.localeCompare(b.date));
  }

  return (
    <div className="dash-card">
      <div className="dash-card__header">
        <h3>Upcoming Legislation</h3>
        {data && <span className="dash-card__badge">{entries.length}</span>}
      </div>
      <div className="dash-card__body dash-card__body--scroll">
        {isLoading && <p className="dash-card__msg">Loading...</p>}
        {isError && <p className="dash-card__msg dash-card__msg--error">Failed to load</p>}
        {data && entries.length === 0 && (
          <p className="dash-card__msg">No upcoming legislation</p>
        )}
        {data && (
          <div className="dash-card__footer-links">
            <Link to="/legislation/pipeline" className="dash-card__footer-link">
              All upcoming legislation →
            </Link>
            <ExplainerButton className="legis-explainer-link legis-explainer-link--dash">
              How EU food law is made
            </ExplainerButton>
          </div>
        )}
        {data && entries.length > 0 && (
          <div className="leg-timeline">
            {entries.map(entry => {
              const days = daysUntil(entry.date);
              if (entry.kind === 'mrl') {
                const item = entry.mrl;
                return (
                  <Link
                    key={`mrl-${item.regulation_number}`}
                    to={`/regulation/${encodeURIComponent(item.regulation_number)}`}
                    className="leg-timeline__item"
                  >
                    <span className={`leg-timeline__dot ${urgencyClass(days)}`} />
                    <div className="leg-timeline__content">
                      <span className="leg-timeline__date">{formatDate(item.application_date)}</span>
                      <span className="leg-timeline__reg">Reg {item.regulation_number}</span>
                      {item.entry_into_force_date && (
                        <span className="leg-timeline__adopted">
                          Adopted {formatDate(item.entry_into_force_date)}
                        </span>
                      )}
                      {item.tier2hazardnames.length > 0 && (
                        <span className="leg-timeline__hazards">
                          {item.tier2hazardnames.slice(0, 3).join(', ')}
                          {item.tier2hazardnames.length > 3 && ` +${item.tier2hazardnames.length - 3} more`}
                        </span>
                      )}
                    </div>
                    <span className="leg-timeline__days">
                      {days <= 0 ? 'Now' : `${days}d`}
                    </span>
                  </Link>
                );
              }
              const item = entry.pipeline;
              return (
                <Link
                  key={`pl-${item.dossier_key}`}
                  to={`/news?tab=eulaw&dossier=${encodeURIComponent(item.dossier_key)}`}
                  className="leg-timeline__item leg-timeline__item--pipeline"
                >
                  <span className={`leg-timeline__dot ${urgencyClass(days)}`} />
                  <div className="leg-timeline__content">
                    <span className="leg-timeline__date">
                      {formatDate(item.application_date)}
                      {!item.application_confirmed && <span className="leg-timeline__expected"> · expected</span>}
                    </span>
                    <span className="leg-timeline__reg">{item.title}</span>
                    <span className="leg-timeline__adopted">
                      Draft{item.basic_act_ref ? ` amending ${item.basic_act_ref}` : ''} · {PIPELINE_STAGE_LABELS[item.stage] || item.stage}
                    </span>
                    {item.hazard_name && (
                      <span className="leg-timeline__hazards">{item.hazard_name}</span>
                    )}
                  </div>
                  <span className="leg-timeline__days">
                    {days <= 0 ? 'Now' : `${days}d`}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
